import { Popover, Spin } from 'antd';
import {
  ROTATION_NO_ZONE_MESSAGE,
  ROTATION_SOWING_MISMATCH_TIP,
} from 'constants/messageConstants';
import {
  AREA_UNIT,
  ROTATION_AREA_CAPTION,
  ROTATION_EMPTY_CELL_LABEL,
  ROTATION_MULTIPLE_SOWING_LABEL,
  ROTATION_NO_SOWING_LABEL,
  ROTATION_NO_YIELD_LABEL,
  ROTATION_PASTE_DRAFT_LABEL,
  ROTATION_PASTE_SKIPPED_LABEL,
  ROTATION_PRODUCTION_CAPTION,
  ROTATION_PRODUCTION_TOTAL_CAPTION,
  ROTATION_RATE_CAPTION,
  ROTATION_SAVED_LABEL,
  ROTATION_SEED_MIX_LABEL,
  ROTATION_SOWING_DATE_FORMAT,
} from 'constants/uiConstants';
import dayjs from 'dayjs';
import React from 'react';
import {
  LuAlertCircle,
  LuAlertTriangle,
  LuBan,
  LuCheck,
  LuLock,
} from 'react-icons/lu';
import MergeClassNames from 'utils/MergeClassNames';
import type { RotationCellDetails } from '../../../../../redux/slices/InsightsSlice';
import IconTip from 'pages/FarmsPage/Workspace/Insights/nutrition/IconTip';
import type { TipText } from 'pages/FarmsPage/Workspace/Insights/nutrition/TipContent';
import { rotationSowingKind, type RotationCell } from './computeRotationGrid';
import { cropColourClasses } from './cropColour';

const yieldFormat = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 1,
});
const tonnesFormat = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0,
});
const rateFormat = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2,
});

/** The plan's area as entered, e.g. "68 ha" — not always the paddock's. */
const areaCaption = (cell: RotationCell): string | null =>
  cell.areaValue == null
    ? null
    : ROTATION_AREA_CAPTION(
        new Intl.NumberFormat(undefined, {
          maximumFractionDigits: cell.areaValue >= 10 ? 0 : 1,
        }).format(cell.areaValue),
        cell.areaUnit ?? AREA_UNIT
      );

const productionCaption = (cell: RotationCell): string => {
  if (cell.productionT == null) return ROTATION_NO_YIELD_LABEL;
  const total = tonnesFormat.format(cell.productionT);
  return cell.yieldTPerHa == null
    ? ROTATION_PRODUCTION_TOTAL_CAPTION(total)
    : ROTATION_PRODUCTION_CAPTION(yieldFormat.format(cell.yieldTPerHa), total);
};

/** Rate per hectare, e.g. "80 kg/ha", or "Seed mix of 3", and the date. */
const sowingCaption = (cell: RotationCell): string => {
  if (cell.shownSowingState === 'multiple') return ROTATION_MULTIPLE_SOWING_LABEL;
  const sowing = cell.shownSowing;
  const parts = [
    sowing?.mix
      ? ROTATION_SEED_MIX_LABEL(sowing.mix.length)
      : sowing?.rate != null
        ? ROTATION_RATE_CAPTION(
            rateFormat.format(sowing.rate),
            sowing.unit?.unit ?? null
          )
        : null,
    sowing?.date && sowing.dateHasDay
      ? dayjs(sowing.date.slice(0, 10)).format(ROTATION_SOWING_DATE_FORMAT)
      : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : ROTATION_NO_SOWING_LABEL;
};

/** The seeds in a shown seed mix, e.g. "Ryegrass · Tetila, White clover", for a tooltip. */
const mixTitle = (cell: RotationCell): string | undefined =>
  cell.shownSowing?.mix
    ?.map((seed) =>
      [seed.cropName, seed.varietyName].filter(Boolean).join(' · ')
    )
    .join(', ');

export interface RotationGridCellProps {
  row: number;
  col: number;
  cell: RotationCell | null;
  /** Which details show under the crop (the Show toggles). */
  details: RotationCellDetails;
  /** The paddock has no zone, so it can't have a plan. */
  noZone: boolean;
  /** An empty second-crop row — not a place a plan can go from the grid. */
  isSpareRow: boolean;
  isPlanningSeason: boolean;
  /** The cell with keyboard focus — where edits and copies start. */
  isActive: boolean;
  /** Part of the selection a paste writes to. */
  isSelected: boolean;
  /** The cell Ctrl/⌘+C copied. */
  isCopied: boolean;
  /** Why this plan can't be changed here right now, or null. */
  lockReason: TipText | null;
  isSaving: boolean;
  isSaved: boolean;
  /** Why the last save of this cell failed, or null. */
  failedReason: string | null;
  /** A paste preview will write here; `cell` is then the draft to show. */
  isDraft: boolean;
  /** Why a paste preview will skip this cell, or null. */
  skipReason: string | null;
  /** The editor, when this cell is the one being edited. */
  editor: React.ReactNode | null;
  onCloseEditor: () => void;
  onSelect: (
    row: number,
    col: number,
    modifiers: { extend: boolean; toggle: boolean }
  ) => void;
  onOpen: (row: number, col: number) => void;
  onContextMenu: (row: number, col: number, x: number, y: number) => void;
}

/**
 * One Rotation planner cell: the plan's crop as a coloured pill (the name is
 * always shown, so colour never carries meaning alone) with its variety on
 * the same line, then — when switched on — production and sowing captions
 * for the selected mode. A missing value says so rather than borrowing the
 * other mode's.
 */
const RotationGridCell: React.FC<RotationGridCellProps> = ({
  row,
  col,
  cell,
  details,
  noZone,
  isSpareRow,
  isPlanningSeason,
  isActive,
  isSelected,
  isCopied,
  lockReason,
  isSaving,
  isSaved,
  failedReason,
  isDraft,
  skipReason,
  editor,
  onCloseEditor,
  onSelect,
  onOpen,
  onContextMenu,
}) => {
  // Muted grey falls just under 4.5:1 on the selection tint, so selected
  // cells use the darker secondary grey.
  const mutedText = isSelected ? 'text-gray-dark' : 'text-gray-muted';
  const renderContent = () => {
    if (noZone) {
      return (
        <span className={`flex items-center gap-1 text-sm ${mutedText}`}>
          <IconTip title={ROTATION_NO_ZONE_MESSAGE}>
            <LuLock size={14} aria-hidden />
          </IconTip>
          {ROTATION_NO_ZONE_MESSAGE.title}
        </span>
      );
    }
    if (isSpareRow) return null;
    if (!cell) {
      return (
        <span
          className={`text-sm ${mutedText}`}
          aria-label={ROTATION_EMPTY_CELL_LABEL}
        >
          —
        </span>
      );
    }
    return (
      <div className="flex min-w-0 flex-col">
        <div className="flex min-w-0 items-center gap-1.5">
          {cell.cropName && (
            // In a narrow column the crop and variety shorten with "…" (the
            // variety first), and the icons and area stay whole.
            <span
              title={cell.cropName}
              className={MergeClassNames(
                'min-w-0 shrink truncate rounded px-1.5 text-base font-semibold',
                cropColourClasses(cell.cropName)
              )}
            >
              {cell.cropName}
            </span>
          )}
          {details.variety && cell.varietyName && (
            <span
              title={cell.varietyName}
              className="min-w-0 shrink-[4] truncate text-base font-medium text-gray-dark"
            >
              {cell.varietyName}
            </span>
          )}
          <span className="ml-auto flex flex-shrink-0 items-center gap-1">
            {cell.sowingMismatch && (
              <IconTip
                title={ROTATION_SOWING_MISMATCH_TIP(
                  cell.sowingMismatch.cropName,
                  cell.sowingMismatch.varietyName,
                  rotationSowingKind(cell)
                )}
              >
                <LuAlertCircle size={14} aria-hidden className="text-danger-dark" />
              </IconTip>
            )}
            {lockReason && (
              <IconTip title={lockReason}>
                <LuLock size={14} aria-hidden />
              </IconTip>
            )}
            {details.area && areaCaption(cell) && (
              <span className={`whitespace-nowrap text-sm ${mutedText}`}>
                {areaCaption(cell)}
              </span>
            )}
          </span>
        </div>
        {details.production && (
          <span className={`truncate text-sm ${mutedText}`}>
            {productionCaption(cell)}
          </span>
        )}
        {details.sowing && (
          <span
            title={mixTitle(cell)}
            className={`truncate text-sm ${mutedText}`}
          >
            {sowingCaption(cell)}
          </span>
        )}
      </div>
    );
  };

  const status = isSaving ? (
    <Spin size="small" />
  ) : skipReason ? (
    <IconTip title={skipReason}>
      <span className="flex items-center gap-1 text-gray-dark">
        <LuBan size={14} aria-hidden />
        {ROTATION_PASTE_SKIPPED_LABEL}
      </span>
    </IconTip>
  ) : isDraft ? (
    <span className="font-semibold text-primary">
      {ROTATION_PASTE_DRAFT_LABEL}
    </span>
  ) : failedReason ? (
    <IconTip title={failedReason}>
      <LuAlertTriangle size={14} aria-hidden className="text-danger-dark" />
    </IconTip>
  ) : isSaved ? (
    <>
      <LuCheck size={14} aria-hidden />
      {ROTATION_SAVED_LABEL}
    </>
  ) : null;

  const content = (
    // Nothing in a cell spills into the next column.
    <div className="relative min-w-0 overflow-hidden">
      {renderContent()}
      {status && (
        <span className="absolute right-0 top-0 flex items-center gap-1 rounded bg-white px-1 text-sm text-charcoal">
          {status}
        </span>
      )}
    </div>
  );

  return (
    <td
      role="gridcell"
      tabIndex={isActive ? 0 : -1}
      aria-selected={isSelected}
      aria-disabled={isSpareRow || undefined}
      data-rotation-cell={`${row}:${col}`}
      className={MergeClassNames(
        'cursor-default select-none border border-gray-bright px-2 py-1 align-top outline-none',
        // The planning year's shading gives way to the selection and to an
        // empty second row, or it would hide them.
        isPlanningSeason && !isSelected && !isSpareRow && 'bg-rowHover',
        isSpareRow && 'rotation-cell-spare',
        isSelected && 'rotation-cell-selected',
        isCopied && 'rotation-cell-copied',
        isDraft && 'rotation-cell-draft',
        skipReason && 'rotation-cell-skipped',
        failedReason && 'rotation-cell-failed',
        isActive && 'rotation-cell-active'
      )}
      onMouseDown={(event) => {
        // Shift+click selects a range, not text.
        if (event.shiftKey) event.preventDefault();
      }}
      onClick={(event) =>
        onSelect(row, col, {
          extend: event.shiftKey,
          toggle: event.ctrlKey || event.metaKey,
        })
      }
      onDoubleClick={() => onOpen(row, col)}
      onContextMenu={(event) => {
        event.preventDefault();
        onContextMenu(row, col, event.clientX, event.clientY);
      }}
    >
      {editor ? (
        <Popover
          open
          trigger="click"
          placement="rightTop"
          destroyOnHidden
          content={editor}
          onOpenChange={(open) => {
            // Stay open while saving, so a failure can still be shown.
            if (!open && !isSaving) onCloseEditor();
          }}
        >
          {content}
        </Popover>
      ) : (
        content
      )}
    </td>
  );
};

export default React.memo(RotationGridCell);
